import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';
import LinkIcon from '@mui/icons-material/Link';
import CheckboxIcon from '@mui/icons-material/CheckBox';
import CheckboxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import axiosInstance from '../../utils/axiosConfig';
import cardTokenService, { CardEditToken } from '../../services/cardTokenService';

interface AthleteEntry {
  _id: string;
  firstname: string;
  surname: string;
  extraCard: boolean;
  extraCardCompetitionGroup: string;
}

interface ClubEntry {
  clubId: string;
  clubName: string;
  athletes: AthleteEntry[];
  activeToken?: CardEditToken;
}

interface WettkampfkartenLinksPanelProps {
  competitionId: string;
}

const formatExpiry = (iso: string) =>
  new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

const buildLink = (token: string) =>
  `${window.location.origin}/card-edit/${token}`;

const WettkampfkartenLinksPanel: React.FC<WettkampfkartenLinksPanelProps> = ({ competitionId }) => {
  const [clubs, setClubs] = useState<ClubEntry[]>([]);
  const [tokens, setTokens] = useState<CardEditToken[]>([]);
  const [loading, setLoading] = useState(true);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [currentClub, setCurrentClub] = useState<ClubEntry | null>(null);
  const [selectedAthletes, setSelectedAthletes] = useState<string[]>([]);
  const [label, setLabel] = useState('');
  const [durationDays, setDurationDays] = useState(3);
  const [secondCardOnly, setSecondCardOnly] = useState(false);
  const [bypassDeadline, setBypassDeadline] = useState(false);
  const [creating, setCreating] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  });

  const [extraCardSectionOpen, setExtraCardSectionOpen] = useState(false);
  const [savingExtraCard, setSavingExtraCard] = useState<string | null>(null);

  const [deregisterDialog, setDeregisterDialog] = useState<{
    open: boolean;
    clubId: string;
    athleteId: string;
    athleteName: string;
  }>({ open: false, clubId: '', athleteId: '', athleteName: '' });
  const [deregisterReason, setDeregisterReason] = useState('');
  const [deregistering, setDeregistering] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [clubsRes, tokensData] = await Promise.all([
        axiosInstance.get(`/api/card-tokens/competition/${competitionId}/clubs-athletes`),
        cardTokenService.getTokens(competitionId)
      ]);

      const activeTokens = tokensData.filter(
        t => !t.isRevoked && new Date(t.expiresAt) > new Date()
      );
      setTokens(tokensData);

      const clubEntries: ClubEntry[] = (clubsRes.data || []).map((c: any) => {
        const activeToken = activeTokens.find(t => {
          const tClub = typeof t.club === 'object' ? t.club._id : t.club;
          return tClub === c.clubId;
        });
        return { ...c, activeToken };
      });

      setClubs(clubEntries);
    } catch (error) {
      console.error('Fehler beim Laden der Panel-Daten:', error);
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateDialog = (club: ClubEntry) => {
    setCurrentClub(club);
    setSelectedAthletes(club.athletes.map(a => a._id));
    setLabel(club.clubName);
    setDurationDays(7);
    setSecondCardOnly(false);
    setBypassDeadline(false);
    setCreateDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!currentClub || selectedAthletes.length === 0) return;
    setCreating(true);
    try {
      const token = await cardTokenService.createToken({
        competitionId,
        clubId: currentClub.clubId,
        athleteIds: selectedAthletes,
        label,
        durationDays,
        secondCardOnly,
        bypassDeadline
      });
      const link = buildLink(token.token);
      await navigator.clipboard.writeText(link);
      setSnackbar({ open: true, message: 'Link erstellt und kopiert!', severity: 'success' });
      setCreateDialogOpen(false);
      loadData();
    } catch {
      setSnackbar({ open: true, message: 'Fehler beim Erstellen des Links.', severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (tokenId: string) => {
    try {
      await cardTokenService.revokeToken(tokenId);
      setSnackbar({ open: true, message: 'Link widerrufen.', severity: 'success' });
      loadData();
    } catch {
      setSnackbar({ open: true, message: 'Fehler beim Widerrufen.', severity: 'error' });
    }
  };

  const handleCopy = async (token: string) => {
    await navigator.clipboard.writeText(buildLink(token));
    setSnackbar({ open: true, message: 'Link kopiert!', severity: 'success' });
  };

  const toggleAthlete = (id: string) => {
    setSelectedAthletes(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const handleCreateAll = async () => {
    const clubsWithoutToken = clubs.filter(c => !c.activeToken && c.athletes.length > 0);
    if (clubsWithoutToken.length === 0) return;
    setCreating(true);
    const links: string[] = [];
    for (const club of clubsWithoutToken) {
      try {
        const token = await cardTokenService.createToken({
          competitionId,
          clubId: club.clubId,
          athleteIds: club.athletes.map(a => a._id),
          label: club.clubName,
          durationDays
        });
        links.push(`${club.clubName}: ${buildLink(token.token)}`);
      } catch {
        // einzelne Fehler ignorieren
      }
    }
    if (links.length > 0) {
      await navigator.clipboard.writeText(links.join('\n'));
      setSnackbar({ open: true, message: `${links.length} Links erstellt und in Zwischenablage kopiert.`, severity: 'success' });
    }
    setCreating(false);
    loadData();
  };

  const openDeregisterDialog = (clubId: string, athleteId: string, athleteName: string) => {
    setDeregisterReason('');
    setDeregisterDialog({ open: true, clubId, athleteId, athleteName });
  };

  const handleDeregisterAthlete = async () => {
    setDeregistering(true);
    try {
      await axiosInstance.post(
        `/api/admin/competitions/${competitionId}/clubs/${deregisterDialog.clubId}/athletes/${deregisterDialog.athleteId}/deregister`,
        { reason: deregisterReason || undefined }
      );
      setSnackbar({ open: true, message: `${deregisterDialog.athleteName} wurde abgemeldet.`, severity: 'success' });
      setDeregisterDialog(d => ({ ...d, open: false }));
      loadData();
    } catch {
      setSnackbar({ open: true, message: 'Fehler beim Abmelden des Sportlers.', severity: 'error' });
    } finally {
      setDeregistering(false);
    }
  };

  const handleExtraCardToggle = async (
    clubId: string,
    athleteId: string,
    extraCard: boolean,
    extraCardCompetitionGroup: string
  ) => {
    setSavingExtraCard(athleteId);
    try {
      await axiosInstance.patch(
        `/api/admin/competitions/${competitionId}/clubs/${clubId}/athletes/${athleteId}/extra-card`,
        { extraCard, extraCardCompetitionGroup }
      );
      setClubs(prev =>
        prev.map(c =>
          c.clubId !== clubId ? c : {
            ...c,
            athletes: c.athletes.map(a =>
              a._id !== athleteId ? a : { ...a, extraCard, extraCardCompetitionGroup }
            )
          }
        )
      );
      setSnackbar({ open: true, message: 'Gespeichert.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Fehler beim Speichern.', severity: 'error' });
    } finally {
      setSavingExtraCard(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const clubsWithoutToken = clubs.filter(c => !c.activeToken && c.athletes.length > 0);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Bearbeitungslinks für Wettkampfkarten</Typography>
        {clubsWithoutToken.length > 0 && (
          <Tooltip title={`Links für alle ${clubsWithoutToken.length} Vereine ohne aktiven Link erstellen`}>
            <Button
              variant="outlined"
              startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
              onClick={handleCreateAll}
              disabled={creating}
              size="small"
            >
              Alle Links erstellen ({clubsWithoutToken.length})
            </Button>
          </Tooltip>
        )}
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Über diese Links können Meldeverantwortliche oder Trainer die Wettkampfkarten ohne Login bearbeiten.
        Links sind zeitlich begrenzt und können jederzeit widerrufen werden.
      </Alert>

      {clubs.length === 0 ? (
        <Typography color="text.secondary">Keine Vereinsmeldungen vorhanden.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Verein</TableCell>
                <TableCell align="center">Sportler</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Gültig bis</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clubs.map(club => (
                <TableRow key={club.clubId}>
                  <TableCell>{club.clubName}</TableCell>
                  <TableCell align="center">{club.athletes.length}</TableCell>
                  <TableCell>
                    {club.activeToken ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip label="Aktiver Link" color="success" size="small" icon={<LinkIcon />} />
                        {club.activeToken.bypassDeadline && (
                          <Chip label="Frist umgangen" color="warning" size="small" variant="outlined" />
                        )}
                      </Box>
                    ) : (
                      <Chip label="Kein Link" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {club.activeToken ? formatExpiry(club.activeToken.expiresAt) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {club.activeToken ? (
                      <>
                        <Tooltip title="Link kopieren">
                          <IconButton size="small" onClick={() => handleCopy(club.activeToken!.token)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Link widerrufen">
                          <IconButton size="small" color="error" onClick={() => handleRevoke(club.activeToken!._id)}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => openCreateDialog(club)}
                        disabled={club.athletes.length === 0}
                      >
                        Link erstellen
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Token-Verlauf (widerrufene / abgelaufene) */}
      {tokens.some(t => t.isRevoked || new Date(t.expiresAt) < new Date()) && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Inaktive Links
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Bezeichnung</TableCell>
                  <TableCell>Verein</TableCell>
                  <TableCell>Abgelaufen / Widerrufen</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tokens
                  .filter(t => t.isRevoked || new Date(t.expiresAt) < new Date())
                  .map(t => (
                    <TableRow key={t._id}>
                      <TableCell>{t.label || '—'}</TableCell>
                      <TableCell>{typeof t.club === 'object' ? t.club.name : '—'}</TableCell>
                      <TableCell>{formatExpiry(t.expiresAt)}</TableCell>
                      <TableCell>
                        {t.isRevoked ? (
                          <Chip label="Widerrufen" color="error" size="small" />
                        ) : (
                          <Chip label="Abgelaufen" size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Sektion: Sportler mit zweiter Wettkampfkarte */}
      <Box sx={{ mt: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <Box
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
          onClick={() => setExtraCardSectionOpen(v => !v)}
        >
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            Zweite Wettkampfkarte (Pflichtübung)
          </Typography>
          {extraCardSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={extraCardSectionOpen}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Hier können Sportler markiert werden, die eine zweite Wettkampfkarte für die Pflichtübung benötigen.
            Die Wettkampfklasse der zweiten Karte wird als Freitext eingetragen und im Export verwendet.
          </Alert>
          {clubs.filter(c => c.athletes.length > 0).map(club => (
            <Paper key={club.clubId} variant="outlined" sx={{ mb: 2 }}>
              <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2">{club.clubName}</Typography>
              </Box>
              <Table size="small">
                <TableBody>
                  {club.athletes.map(athlete => (
                    <TableRow key={athlete._id}>
                      <TableCell>{athlete.firstname} {athlete.surname}</TableCell>
                      <TableCell align="center" sx={{ width: 80 }}>
                        {savingExtraCard === athlete._id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Switch
                            size="small"
                            checked={athlete.extraCard}
                            onChange={e =>
                              handleExtraCardToggle(
                                club.clubId,
                                athlete._id,
                                e.target.checked,
                                athlete.extraCardCompetitionGroup
                              )
                            }
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        {athlete.extraCard && (
                          <TextField
                            size="small"
                            label="Wettkampfklasse (2. Karte)"
                            value={athlete.extraCardCompetitionGroup}
                            onChange={e => {
                              setClubs(prev =>
                                prev.map(c =>
                                  c.clubId !== club.clubId ? c : {
                                    ...c,
                                    athletes: c.athletes.map(a =>
                                      a._id !== athlete._id ? a : { ...a, extraCardCompetitionGroup: e.target.value }
                                    )
                                  }
                                )
                              );
                            }}
                            onBlur={() =>
                              handleExtraCardToggle(
                                club.clubId,
                                athlete._id,
                                athlete.extraCard,
                                athlete.extraCardCompetitionGroup
                              )
                            }
                            fullWidth
                          />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ width: 48 }}>
                        <Tooltip title="Sportler abmelden">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openDeregisterDialog(club.clubId, athlete._id, `${athlete.firstname} ${athlete.surname}`)}
                          >
                            <PersonRemoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ))}
        </Collapse>
      </Box>

      {/* Dialog: Link erstellen */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Link für {currentClub?.clubName} erstellen</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Bezeichnung (optional)"
            value={label}
            onChange={e => setLabel(e.target.value)}
            size="small"
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Gültigkeitsdauer</InputLabel>
            <Select
              value={durationDays}
              label="Gültigkeitsdauer"
              onChange={e => setDurationDays(Number(e.target.value))}
            >
              <MenuItem value={1}>1 Tag</MenuItem>
              <MenuItem value={2}>2 Tage</MenuItem>
              <MenuItem value={3}>3 Tage</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={secondCardOnly}
                onChange={e => setSecondCardOnly(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Nur 2. Wettkampfkarte zur Bearbeitung freigeben
              </Typography>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={bypassDeadline}
                onChange={e => setBypassDeadline(e.target.checked)}
                size="small"
                color="warning"
              />
            }
            label={
              <Typography variant="body2">
                Bearbeitung auch nach Abgabefrist erlauben
              </Typography>
            }
          />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2">Sportler auswählen</Typography>
            {currentClub?.athletes.some(a => a.extraCard) && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSelectedAthletes(
                    currentClub.athletes.filter(a => a.extraCard).map(a => a._id)
                  );
                  setSecondCardOnly(true);
                }}
              >
                Nur 2. Karte
              </Button>
            )}
          </Box>
          <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto' }}>
            {currentClub?.athletes.map(a => {
              const selected = selectedAthletes.includes(a._id);
              return (
                <Box
                  key={a._id}
                  onClick={() => toggleAthlete(a._id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 2, py: 1, cursor: 'pointer',
                    bgcolor: selected ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  {selected ? <CheckboxIcon color="primary" fontSize="small" /> : <CheckboxOutlineBlankIcon fontSize="small" />}
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>{a.firstname} {a.surname}</Typography>
                  {a.extraCard && (
                    <Chip label="2. Karte" size="small" color="secondary" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                  )}
                </Box>
              );
            })}
          </Paper>
          <Typography variant="caption" color="text.secondary">
            {selectedAthletes.length} Sportler ausgewählt
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={selectedAthletes.length === 0 || creating}
            startIcon={creating ? <CircularProgress size={16} /> : <ContentCopyIcon />}
          >
            Link erstellen & kopieren
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Sportler abmelden */}
      <Dialog open={deregisterDialog.open} onClose={() => setDeregisterDialog(d => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Sportler abmelden</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Alert severity="warning">
            <strong>{deregisterDialog.athleteName}</strong> wird vom Wettkampf abgemeldet. Diese Aktion kann nicht rückgängig gemacht werden.
          </Alert>
          <TextField
            label="Grund (optional)"
            value={deregisterReason}
            onChange={e => setDeregisterReason(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeregisterDialog(d => ({ ...d, open: false }))}>Abbrechen</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeregisterAthlete}
            disabled={deregistering}
            startIcon={deregistering ? <CircularProgress size={16} /> : <PersonRemoveIcon />}
          >
            Abmelden
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WettkampfkartenLinksPanel;
